import React, { FC, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LinkIcon from '@mui/icons-material/Link';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InventoryIcon from '@mui/icons-material/Inventory';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import BarChartIcon from '@mui/icons-material/BarChart';
import CategoryIcon from '@mui/icons-material/Category';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SupportIcon from '@mui/icons-material/Support';
import PaymentsIcon from '@mui/icons-material/Payments';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { Typography, Tooltip, Box } from '@mui/material';
import { useIsAdmin } from 'src/hooks/useIsAdmin';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useTrialStatus } from 'src/hooks/useTrialStatus';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import { ResponsiveDrawer } from 'src/components/Drawer';
import {
  CloseButton,
  NavLinks,
  SidebarNav,
  ToggleButton,
} from 'src/components/Drawer/NavSidebarDrawer/styled';
import { NavLink, SidebarDrawerLink } from 'src/components/Link/styled';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useLocation } from 'react-router-dom';
import { clearStorage } from 'src/utils/localStorage';

export const NavSidebarDrawer: FC = () => {
  const client = useApolloClient();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { collapsed, setCollapsed } = useSidebar();
  const isAdmin = useIsAdmin();
  const { user } = useAccount();
  const { isTrialExpired, shouldFreeze } = useTrialStatus();
  const canSeeDeckStudio = Boolean(user?.deckStudioEnabled);
  const companyName = (user?.company?.name || '').trim();

  const settingsPath = `/${ROUTE_SOURCES.SETTINGS}`;
  const settingsTab = new URLSearchParams(location.search).get('tab');
  const isPricingDrawerActive =
    location.pathname === settingsPath && settingsTab === 'pricing';
  const isSettingsDrawerActive =
    location.pathname === settingsPath && settingsTab !== 'pricing';

  const handleClose = () => setMobileOpen(false);
  const handleMenuClick = () => setMobileOpen(!mobileOpen);
  const handleToggle = () => setCollapsed((prev) => !prev);

  const handleLogout = () => {
    clearStorage();
    client.clearStore();
  };

  return (
    <ResponsiveDrawer
      mobileOpen={mobileOpen}
      collapsed={collapsed}
      onClose={handleClose}
      onMenuClick={handleMenuClick}
    >
      <SidebarNav collapsed={collapsed}>
        {collapsed ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              height: '48px',
              marginBottom: '24px',
              paddingTop: '8px',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                color: 'rgba(30, 237, 159, 1)',
                fontSize: '28px',
                fontWeight: 700,
                fontFamily: 'Sarabun',
              }}
            >
              K
            </Box>
            <ToggleButton
              onClick={handleToggle}
              color="secondary"
              size="small"
              collapsed={collapsed}
              sx={{
                position: 'relative',
                top: 'auto',
                right: 'auto',
                bottom: 'auto',
                transform: 'none',
                width: '28px',
                height: '28px',
                minWidth: '28px',
              }}
            >
              <ChevronRightIcon />
            </ToggleButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              position: 'relative',
              marginBottom: '24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minHeight: '48px',
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  flex: 1,
                  overflow: 'visible',
                  textOverflow: 'clip',
                  whiteSpace: 'nowrap',
                  marginBottom: 0,
                }}
              >
                Kipify
              </Typography>
              <ToggleButton
                onClick={handleToggle}
                color="secondary"
                size="small"
                collapsed={collapsed}
              >
                <ChevronLeftIcon />
              </ToggleButton>
            </Box>
            {companyName ? (
              <Typography
                component="p"
                title={companyName}
                sx={{
                  mt: 0.5,
                  mb: 0,
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: 'rgba(255, 255, 255, 0.72)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {companyName}
              </Typography>
            ) : null}
          </Box>
        )}
        <CloseButton onClick={handleClose} color="secondary" size="large">
          <CloseIcon />
        </CloseButton>
        <NavLinks collapsed={collapsed}>
          <Tooltip
            title={
              collapsed
                ? 'Connectors'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.CONNECTORS}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <LinkIcon />
                {!collapsed && 'Connectors'}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Dashboard'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.DASHBOARD}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <DashboardIcon />
                {!collapsed && 'Dashboard'}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Custom report'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.CUSTOM_REPORT}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <AssessmentIcon />
                {!collapsed && 'Custom report'}
              </NavLink>
            </div>
          </Tooltip>
          {canSeeDeckStudio && (
            <Tooltip
              title={
                collapsed
                  ? 'Studio deck (lab)'
                  : isTrialExpired || shouldFreeze
                    ? 'Trial expired - Upgrade plan to unlock'
                    : 'Crea il prompt per il deck dai tuoi dati'
              }
              placement="right"
            >
              <div>
                <NavLink
                  to={`/${ROUTE_SOURCES.AI_DECK_STUDIO}`}
                  onClick={handleClose}
                  collapsed={collapsed}
                  sx={{
                    opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                    pointerEvents:
                      isTrialExpired || shouldFreeze ? 'none' : 'auto',
                  }}
                >
                  <SlideshowIcon />
                  {!collapsed && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Studio deck
                      <Box
                        sx={{
                          backgroundColor: '#6366f1',
                          color: 'white',
                          fontSize: '0.5rem',
                          fontWeight: 600,
                          px: 0.5,
                          py: 0.1,
                          borderRadius: 0.5,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px',
                          lineHeight: 1,
                        }}
                      >
                        Lab
                      </Box>
                    </Box>
                  )}
                </NavLink>
              </div>
            </Tooltip>
          )}
          <Tooltip
            title={
              collapsed
                ? 'KPI'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.KPI}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <AnalyticsIcon />
                {!collapsed && 'KPI'}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Finance'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.FINANCE}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <AccountBalanceIcon />
                {!collapsed && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Finance
                    <Box
                      sx={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        fontSize: '0.5rem',
                        fontWeight: 600,
                        px: 0.5,
                        py: 0.1,
                        borderRadius: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        lineHeight: 1,
                      }}
                    >
                      NEW
                    </Box>
                  </Box>
                )}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Inventory'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.INVENTORY}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <InventoryIcon />
                {!collapsed && 'Inventory'}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Restocked' : ''} placement="right">
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.INVENTORY_RESTOCKED}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                  pl: collapsed ? 0 : 3.25,
                  minHeight: collapsed ? '44px' : 32,
                  py: collapsed ? undefined : 0.75,
                  fontSize: collapsed ? undefined : 13,
                  color: collapsed ? undefined : 'rgba(255, 255, 255, 0.72)',
                  gap: collapsed ? undefined : '6px',
                  borderRadius: collapsed ? undefined : '8px',
                  position: 'relative',
                  '&::before': collapsed
                    ? undefined
                    : {
                        content: '""',
                        position: 'absolute',
                        left: 18,
                        top: '50%',
                        width: 8,
                        height: 1,
                        background: 'rgba(255, 255, 255, 0.22)',
                        transform: 'translateY(-50%)',
                      },
                  '&:hover': {
                    backgroundColor: collapsed
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(255, 255, 255, 0.06)',
                  },
                  '&.active': {
                    color: 'rgba(30, 237, 159, 1)',
                    backgroundColor: 'rgba(30, 237, 159, 0.10)',
                    '&::before': collapsed
                      ? undefined
                      : {
                          content: '""',
                          position: 'absolute',
                          left: 18,
                          top: '50%',
                          width: 10,
                          height: 2,
                          background: 'rgba(30, 237, 159, 0.65)',
                          transform: 'translateY(-50%)',
                          borderRadius: 99,
                        },
                  },
                }}
              >
                <FiberManualRecordIcon
                  sx={{
                    fontSize: collapsed ? 20 : 10,
                    opacity: collapsed ? 1 : 0.9,
                  }}
                />
                {!collapsed && <Box>Restocked</Box>}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={collapsed ? 'Products analysis' : ''}
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.PRODUCTS_ANALYSIS}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <BarChartIcon />
                {!collapsed && <Box>Products analysis</Box>}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={collapsed ? 'Category analysis' : ''}
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.CATEGORY_ANALYSIS}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <ShowChartIcon />
                {!collapsed && <Box>Category analysis</Box>}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Bundles'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.BUNDLES}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <CategoryIcon />
                {!collapsed && 'Bundles'}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Retention'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.RETENTION}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <AutorenewRoundedIcon />
                {!collapsed && <Box>Retention</Box>}
              </NavLink>
            </div>
          </Tooltip>
          <Tooltip
            title={
              collapsed
                ? 'Support'
                : isTrialExpired || shouldFreeze
                  ? 'Trial expired - Upgrade plan to unlock'
                  : ''
            }
            placement="right"
          >
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.SUPPORT}`}
                onClick={handleClose}
                collapsed={collapsed}
                sx={{
                  opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                  pointerEvents:
                    isTrialExpired || shouldFreeze ? 'none' : 'auto',
                }}
              >
                <SupportIcon />
                {!collapsed && 'Support'}
              </NavLink>
            </div>
          </Tooltip>
          {isAdmin && (
            <Tooltip
              title={
                collapsed
                  ? 'Team'
                  : isTrialExpired || shouldFreeze
                    ? 'Trial expired - Upgrade plan to unlock'
                    : ''
              }
              placement="right"
            >
              <div>
                <NavLink
                  to={`/${ROUTE_SOURCES.TEAM_MANAGEMENT}`}
                  onClick={handleClose}
                  collapsed={collapsed}
                  sx={{
                    opacity: isTrialExpired || shouldFreeze ? 0.5 : 1,
                    pointerEvents:
                      isTrialExpired || shouldFreeze ? 'none' : 'auto',
                  }}
                >
                  <GroupIcon />
                  {!collapsed && 'Team'}
                </NavLink>
              </div>
            </Tooltip>
          )}
          <Tooltip title={collapsed ? 'Pricing' : ''} placement="right">
            <div>
              <SidebarDrawerLink
                to={`/${ROUTE_SOURCES.SETTINGS}?tab=pricing`}
                onClick={handleClose}
                collapsed={collapsed}
                data-nav-active={isPricingDrawerActive ? 'true' : undefined}
              >
                <PaymentsIcon />
                {!collapsed && 'Pricing'}
              </SidebarDrawerLink>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Settings' : ''} placement="right">
            <div>
              <SidebarDrawerLink
                to={`/${ROUTE_SOURCES.SETTINGS}`}
                onClick={handleClose}
                collapsed={collapsed}
                data-nav-active={isSettingsDrawerActive ? 'true' : undefined}
              >
                <SettingsIcon />
                {!collapsed && 'Settings'}
              </SidebarDrawerLink>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Logout' : ''} placement="right">
            <div>
              <NavLink
                to={`/${ROUTE_SOURCES.LOGIN}`}
                onClick={handleLogout}
                collapsed={collapsed}
              >
                <LogoutIcon />
                {!collapsed && 'Logout'}
              </NavLink>
            </div>
          </Tooltip>
        </NavLinks>
      </SidebarNav>
    </ResponsiveDrawer>
  );
};
